import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Plus, Minus, X, Check, Camera, SwitchCamera,
  Flashlight, FlashlightOff, Loader2
} from "lucide-react";
import QRCode from "react-qr-code";
import { getVariantLabels } from "../../lib/boutiqueType";
import { paymentLabel } from "../../lib/payments";

// ─── Manual Entry Dialog ──────────────────────────────────────────────────────
export function ManualEntryDialog({
  open, onOpenChange, manualEntry, setManualEntry, categories, devise, primaryColor, onSubmit
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ajouter un article</DialogTitle>
          <DialogDescription>Article non répertorié dans le stock</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Désignation *</Label>
            <Input value={manualEntry.designation}
              onChange={(e) => setManualEntry({ ...manualEntry, designation: e.target.value })}
              placeholder="Nom de l'article" data-testid="manual-designation" />
          </div>
          <div className="space-y-2">
            <Label>Prix unitaire * ({devise})</Label>
            <Input type="number" step="1" value={manualEntry.unitPrice}
              onChange={(e) => setManualEntry({ ...manualEntry, unitPrice: e.target.value })}
              placeholder="0" data-testid="manual-price" />
          </div>
          <div className="space-y-2">
            <Label>Quantité</Label>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon"
                onClick={() => setManualEntry({ ...manualEntry, quantity: Math.max(1, manualEntry.quantity - 1) })}>
                <Minus className="w-4 h-4" />
              </Button>
              <span className="w-12 text-center font-medium">{manualEntry.quantity}</span>
              <Button variant="outline" size="icon"
                onClick={() => setManualEntry({ ...manualEntry, quantity: manualEntry.quantity + 1 })}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Catégorie (optionnel)</Label>
            <Select value={manualEntry.category}
              onValueChange={(value) => setManualEntry({ ...manualEntry, category: value })}>
              <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.icon} {cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={onSubmit} style={{ backgroundColor: primaryColor }} className="text-white"
            data-testid="manual-add-btn">
            Ajouter au panier →
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Discount Dialog ──────────────────────────────────────────────────────────
export function DiscountDialog({ open, onOpenChange, discountInput, setDiscountInput, primaryColor, onApply }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Ajouter une remise</DialogTitle></DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-3 gap-2">
            {[10, 15, 20].map((pct) => (
              <Button key={pct} variant="outline" onClick={() => onApply("percent", pct)}>{pct}%</Button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input type="number" value={discountInput}
              onChange={(e) => setDiscountInput(e.target.value)} placeholder="Montant ou %" />
            <Button onClick={() => {
              const val = parseFloat(discountInput);
              if (val <= 100) onApply("percent", val);
              else onApply("fixed", val);
            }} style={{ backgroundColor: primaryColor }} className="text-white">
              Appliquer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Variant Selection Dialog ─────────────────────────────────────────────────
export function VariantDialog({
  open, onOpenChange, selectedProduct, selectedVariant, setSelectedVariant, primaryColor, onConfirm
}) {
  const labels = getVariantLabels();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Choisir la {labels.size.toLowerCase()}</DialogTitle>
          <DialogDescription>{selectedProduct?.name}</DialogDescription>
        </DialogHeader>
        {selectedProduct && (
          <div className="py-4">
            <div className="grid grid-cols-4 gap-2">
              {selectedProduct.variants?.map((variant) => {
                const isAvailable = variant.quantity > 0;
                const isSelected = selectedVariant?.id === variant.id;
                return (
                  <button key={variant.id} onClick={() => isAvailable && setSelectedVariant(variant)}
                    disabled={!isAvailable} data-testid={`variant-${variant.id}`}
                    className={`p-3 rounded-lg text-center transition-all border ${
                      isSelected ? "ring-2 text-white" :
                      isAvailable ? "bg-secondary hover:bg-secondary/80" : "opacity-50 cursor-not-allowed"
                    }`}
                    style={isSelected ? { backgroundColor: primaryColor, "--tw-ring-color": primaryColor } : {}}>
                    <p className="font-bold">{variant.size}</p>
                    <p className="text-xs mt-1 opacity-70">{isAvailable ? `(${variant.quantity})` : "Rupture"}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={onConfirm} disabled={!selectedVariant}
            style={{ backgroundColor: primaryColor }} className="text-white">
            Ajouter au panier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Scanner Dialog ───────────────────────────────────────────────────────────
export function ScannerDialog({
  open, onClose, videoRef, cameraLoading, cameraError, scanning, scanFeedback,
  availableCameras, torchSupported, torchEnabled, switchCamera, toggleTorch
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden" data-testid="scanner-dialog">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5" /> Scanner un code-barres
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          <div className={`relative aspect-[4/3] bg-black overflow-hidden transition-all duration-300 ${
            scanFeedback?.type === 'success' ? 'ring-4 ring-green-500' :
            scanFeedback?.type === 'error' ? 'ring-4 ring-red-500' : ''
          }`}>
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted autoPlay />
            {cameraLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                <div className="text-center text-white">
                  <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" />
                  <p className="text-sm">{cameraError || "Initialisation..."}</p>
                </div>
              </div>
            )}
            {!cameraLoading && cameraError && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/90">
                <div className="text-center text-white px-6">
                  <Camera className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">{cameraError}</p>
                </div>
              </div>
            )}
            {scanning && !scanFeedback && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-32 border-2 border-white/70 rounded-lg relative">
                  <div className="absolute top-0 left-0 w-4 h-4 border-t-4 border-l-4 border-white rounded-tl" />
                  <div className="absolute top-0 right-0 w-4 h-4 border-t-4 border-r-4 border-white rounded-tr" />
                  <div className="absolute bottom-0 left-0 w-4 h-4 border-b-4 border-l-4 border-white rounded-bl" />
                  <div className="absolute bottom-0 right-0 w-4 h-4 border-b-4 border-r-4 border-white rounded-br" />
                </div>
              </div>
            )}
            {scanFeedback && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className={`px-4 py-2 rounded-lg ${
                  scanFeedback.type === 'success' ? 'bg-green-600' : 'bg-red-600'
                } text-white font-medium`}>
                  {scanFeedback.type === 'success' ? <Check className="w-5 h-5 inline mr-2" /> : <X className="w-5 h-5 inline mr-2" />}
                  {scanFeedback.message}
                </div>
              </div>
            )}
            <div className="absolute top-3 right-3 flex gap-2">
              {availableCameras.length > 1 && (
                <Button variant="secondary" size="icon" className="h-10 w-10 rounded-full bg-black/60" onClick={switchCamera}>
                  <SwitchCamera className="w-5 h-5 text-white" />
                </Button>
              )}
              {torchSupported && (
                <Button variant="secondary" size="icon"
                  className={`h-10 w-10 rounded-full ${torchEnabled ? 'bg-yellow-500' : 'bg-black/60'}`}
                  onClick={toggleTorch}>
                  {torchEnabled ? <Flashlight className="w-5 h-5" /> : <FlashlightOff className="w-5 h-5 text-white" />}
                </Button>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="p-4 pt-2">
          <Button variant="outline" onClick={onClose} className="w-full">
            <X className="w-4 h-4 mr-2" /> Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Table Orders Dialog (commandes en salle) ────────────────────────────────
export function TableOrdersDialog({ open, onOpenChange, orders, formatPrice, onLoad, onCancel, primaryColor }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="table-orders-dialog">
        <DialogHeader>
          <DialogTitle>Commandes en salle</DialogTitle>
          <DialogDescription>Commandes prises par les serveurs, en attente d'encaissement</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3 py-2">
          {orders.length === 0 ? (
            <p className="text-center text-muted-foreground py-8 text-sm">Aucune commande en attente</p>
          ) : orders.map((order) => (
            <div key={order.id} className="p-3 rounded-lg border space-y-2" data-testid={`table-order-${order.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm">{order.table_name}</p>
                  <p className="text-xs text-muted-foreground">
                    Pris par <span className="font-medium">{order.serveur?.name || "Serveur"}</span>
                    {" · "}{new Date(order.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
                <span className="font-bold">{formatPrice(order.total)}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {order.items.map((i) => `${i.quantity}× ${i.product_name}`).join(", ")}
              </p>
              {order.note && <p className="text-xs italic text-muted-foreground">Note : {order.note}</p>}
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 text-white" style={{ backgroundColor: primaryColor }}
                  onClick={() => onLoad(order)} data-testid={`load-order-${order.id}`}>
                  Charger dans le panier
                </Button>
                <Button size="sm" variant="outline" className="text-destructive hover:text-destructive"
                  onClick={() => onCancel(order.id)} data-testid={`cancel-order-${order.id}`}>
                  Annuler
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Invoice Success Dialog ───────────────────────────────────────────────────
export function InvoiceDialog({ open, onOpenChange, lastInvoice, formatPrice, userRole }) {
  if (!lastInvoice) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-600">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.6 }}>
              <Check className="w-8 h-8" />
            </motion.div>
            Vente enregistrée !
          </DialogTitle>
        </DialogHeader>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <div className="text-center p-4 bg-secondary/50 rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">Facture</p>
            <p className="text-xl font-bold">{lastInvoice.invoice_number}</p>
            <p className="text-3xl font-bold mt-2" style={{ color: '#FF4500' }}>{formatPrice(lastInvoice.total)}</p>
            <p className="text-xs text-muted-foreground mt-2" data-testid="invoice-payment-label">
              Payé en {paymentLabel(lastInvoice)}{lastInvoice.payment_reference ? ` · réf. ${lastInvoice.payment_reference}` : ''}
            </p>
          </div>
          {lastInvoice.payment_method === 'cash' && lastInvoice.change_given > 0 && (
            <div className="p-4 rounded-lg text-center" style={{ backgroundColor: '#FF4500' }}>
              <p className="text-sm" style={{ color: '#FFFFFF' }}>Monnaie rendue</p>
              <p className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>{formatPrice(lastInvoice.change_given)}</p>
            </div>
          )}
          <div className="flex justify-center">
            <div className="p-3 bg-white rounded-lg">
              <QRCode value={`${window.location.origin}/invoice/${lastInvoice.id}`} size={120} />
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground">Scannez pour voir la facture</p>
        </motion.div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="w-full text-white" style={{ backgroundColor: '#FF4500' }}>
            Nouvelle vente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
